import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BehaviorSubject, Observable } from "rxjs";
import { Comment } from "../../model/comments";
import { Messages } from "../../model/messages";
import { CommentsService } from "../../services/comments.service";
import { AuthService } from "../../auth.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../services/navigation.helper";
import { Navigation } from "../../model/navigation";
import { ActionButtonSubmit } from "../../model/action-button-submit";
import { TicketService } from "../../services/ticket.service";
import { Ticket } from "../../model/ticket";
import { Editor } from "primeng/editor";
@Component({
  selector: "comments-details",
  templateUrl: "./comments-details.component.html",
  styleUrls: ["./comments-details.component.css"],
})
export class CommentsDetailsComponent implements OnInit {
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  comments = new BehaviorSubject<Comment>(new Comment(0, 0, "", ""));
  editMode: boolean = false;
  showNewCommentForm: boolean = true;
  commentsForm: FormGroup;
  commentsTitle: string = "commentsDetails.title.comments.details";
  @Input() currentTicket: Observable<Ticket>;
  ticket: Ticket | null = null;
  currentId: number | null;
  isLoading: boolean = false;
  comment: BehaviorSubject<Comment[]> = new BehaviorSubject<Comment[]>([]);
  @Input() selectedComment: Comment | null = null;
  completionUrl = new BehaviorSubject<string>("");
  @Input() editModeComment: boolean = true;

  @ViewChild("editor") editor: Editor;
  constructor(
    private ticketService: TicketService,
    private commentService: CommentsService,
    private auth: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private nav: NavigationHelper
  ) {}

  ngOnInit(): void {
    this.editMode = this.editModeComment;

    this.commentService._currentComments.subscribe((comments) => {
      this.comment.next(comments);
    });
    this.initializeForm();
    if (this.currentTicket) {
      this.currentTicket.subscribe((ticket) => {
        this.currentId = ticket.id;
        this.ticket = ticket;
      });
    }

    if (Navigation.NEW.toString() === this.nav.currentId) {
      this.editMode = this.editModeComment;
      this.commentsForm.reset();
      this.commentsTitle = "commentsDetails.title.new";
    } else if (this.editModeComment) {
      this.editMode = this.editModeComment;
      this.commentsTitle = "commentsDetails.title.edit";
    }

    this.commentService.selectedCommentObservable.subscribe(
      (selectedComment) => {
        if (selectedComment) {
          this.populateForm(selectedComment);
          this.completionUrl.next(
            "tickets/" +
              this.currentId +
              "/fragmentedit/comment/" +
              selectedComment.id
          );
        }
      }
    );

    if (
      !(
        this.commentService.currentComment &&
        this.commentService.currentComment.id &&
        this.commentService.currentComment.id > 0
      )
    ) {
      this.commentService.selectFromRouter();
    }
    this.nav.fragmentId.subscribe((id) => {
      if (
        this.nav.currentFragmentTopic === "comment" &&
        (id === "-9" || Number(id) > 0)
      ) {
        const fragmentId = Number(id);

        if (fragmentId > 0) {
          const action = this.nav.currentAction;

          if (action === "fragment" && !this.editModeComment) {
            this.editMode = false;
          } else if (
            action === "fragmentedit" &&
            fragmentId === Navigation.NEW
          ) {
            this.editMode = true;
          } else if (action === "fragmentedit") {
            this.editMode = true;
          }
        }
      }
    });
  }

  private initializeForm(): void {
    this.commentsForm = this.fb.group({
      description: "",
    });
  }

  private populateForm(comment: Comment): void {
    this.comments.next(comment);

    const description = comment.content || comment.name || "";

    this.commentsForm.patchValue({
      description: description,
    });

    if (!this.editModeComment) {
      this.editMode = comment.id === Navigation.NEW;
    }

    this.commentsTitle = this.editMode
      ? "commentsDetails.title.new"
      : "commentsDetails.title.comments.details";
  }

  autoResize(event: any) {
    if (this.editor && this.editor.quill) {
      const editorElem = this.editor.quill.root as HTMLElement;
      if (editorElem) {
        editorElem.style.height = "auto";
        editorElem.style.height = editorElem.scrollHeight + "px";
      }
    }
  }

  toggleEditMode() {
    if (this.editMode) {
      this.editMode = false;
      this.comments.next(Comment.EMPTY);
      this.commentsForm.reset();
      this.commentsTitle = "commentsDetails.title";
    } else {
      this.router.navigate([
        "tickets/" + this.currentId + "/fragmentedit/comment/" + this.commentId,
      ]);
      this.editMode = true;
    }
  }

  saveComment(action: ActionButtonSubmit): void {
    this.isLoading = true;
    if (this.commentsForm.valid) {
      const token = this.auth.token;
      const id = this.comments.value.id;
      const ticketId = this.currentId;

      if (ticketId == null) {
        return;
      }
      const description: string = this.commentsForm.value.description || "";
      const title =
        description.length > 60 ? description.substring(0, 60) : description;
      const content = description;
      const updatedComment = new Comment(
        id && id >= 1 ? id : Navigation.NEW,
        ticketId,
        title,
        content
      );

      if (updatedComment.id === null || updatedComment.id < 1) {
        this.commentService
          .createComment(token, ticketId, updatedComment)
          .subscribe((messages) => {
            this.receiveComment(messages, action, ticketId, token);
          });
      } else {
        this.commentService
          .updateComment(token, ticketId, updatedComment)
          .subscribe((messages) => {
            this.receiveComment(messages, action, ticketId, token);
          });
      }
    }
  }

  receiveComment(
    messages: Messages,
    action: ActionButtonSubmit,
    ticketId: number,
    token: string
  ) {
    this.messages.next(messages);
    if (messages.hasAnyMessages) {
      action.result(false);
      this.isLoading = false;
    } else {
      action.result(true);
      this.ticketService
        .getTicketWithParts(token, ticketId)
        .subscribe((messages) => {
          this.messages.next(messages);
          const updatedTicket = this.ticketService.currentTicket;
          const updatedComments = updatedTicket?.parts?.comments ?? [];
          const lastComment = updatedComments[updatedComments.length - 1];
          this.commentService.accept(updatedComments);
          this.isLoading = false;
          const navigateUrl = [
            "tickets/" +
              this.currentId +
              "/fragmentedit/comment/" +
              Navigation.NEW,
          ];
          this.router.navigate(navigateUrl);
        });
    }
  }

  public deleteComment(): void {
    const token = this.auth.token;
    const commentId = this.comments.value.id;
    const ticketId = this.comments.value.ticketId;

    if (commentId != null && ticketId != null) {
      this.commentService.deleteComment(token, ticketId, commentId).subscribe(
        (response) => {
          this.messages.next(response);
          this.ticketService
            .getTicketWithParts(token, ticketId)
            .subscribe((messages) => {
              this.messages.next(messages);
            });
          this.comments.next(Comment.EMPTY);
        },
        (error) => {
          this.messages.next(
            Messages.from({ status: ["comments.delete.failed"] })
          );
        }
      );
    }
  }
  autoGrow(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  }

  get commentId(): number {
    const comment = this.comments.value;
    return comment && comment.id ? comment.id : Navigation.NEW;
  }

  get TRUE(): boolean {
    return true;
  }
  get FALSE(): boolean {
    return false;
  }
}
